import { parseIsoInstant } from './timeGrid'
import { STATUS_CHIP, TABLE_LABEL } from './historyView'

// MessageCatalog (03-dominio.md, RN75): from a failure to a UiMessage `{key, params, actions, extra, placement}` where `key` is the
// ENGLISH text of the i18n catalogue (T-6: the literal of Sun is mapped here, it is never a key).
//   actions: PRESENCE_ONLY | RELOAD_TIMELINE | TRY_AGAIN (what button helps), `extra`: a second sentence (RH-19),
//   placement: where it is shown (`panel`, or next to the `reason` / `comment` field).
// The reading half answers #189 §5, the writing half #201 §5. Parameters come out already readable (RN77): ids as names, instants
// in local time through `context.format`, lists joined with commas.

const READ_500_KEY = 'The timeline could not be read. A frequent cause is a plant record that ends before it starts, ask support to check it.'
const ACTOR_KEY = 'Your session does not belong to a plant user (connector or cluster node), so it cannot see or correct timelines.'

/** TOO_MANY_ROWS (400 INVALID_INTENT, reason TOO_MANY_ROWS): the density limit of a lane (RN17). */
export const isTooManyRows = (failure) => failure.kind === 'contract' &&
  failure.message === 'DATA_CORRECTION_INVALID_INTENT' && failure.details.reason === 'TOO_MANY_ROWS'

const describeTooManyRows = (failure, { deviceName = () => '', atMinimum = false }) => {
  const deviceId = failure.details.deviceId
  const params = { device: deviceName(deviceId) || `#${deviceId}`, maxRows: failure.details.maxRows }
  return {
    key: atMinimum
      ? 'Machine {{device}} has too many records even in one hour (more than {{maxRows}} in a lane).'
      : 'Machine {{device}} has too many records in this period (more than {{maxRows}} in a lane), so a shorter period is shown.',
    params
  }
}

const READ_CONTRACT = {
  DATA_CORRECTION_ACTOR_NOT_A_USER: ACTOR_KEY,
  DATA_CORRECTION_DEVICE_NOT_FOUND: 'The machine does not exist.',
  DATA_CORRECTION_WORKER_NOT_FOUND: 'The worker does not exist.'
}

/** The message of a failed reading (GET timeline), with `context = { deviceName(id), atMinimum }`. */
export const describeReadFailure = (failure, context = {}) => {
  if (failure.kind === 'springForbidden') return { key: 'You do not have permission to see the timelines.', params: {} }
  if (failure.kind === 'contract') {
    if (failure.message === 'DATA_CORRECTION_INVALID_INTENT') {
      if (isTooManyRows(failure)) return describeTooManyRows(failure, context)
      if (failure.details.reason === 'RANGE_TOO_LONG') return { key: 'The period cannot be longer than {{maxDays}} days.', params: { maxDays: failure.details.maxDays } }
      return { key: 'Invalid request ({{reason}}).', params: { reason: failure.details.reason } }
    }
    if (READ_CONTRACT[failure.message]) return { key: READ_CONTRACT[failure.message], params: {} }
    return { key: 'Unexpected error ({{code}}).', params: { code: failure.message } }
  }
  if (failure.status >= 500) return { key: READ_500_KEY, params: {} }
  return { key: 'Unexpected error ({{code}}).', params: { code: failure.message || failure.status } }
}

// ---- History: the list (#207 §4) and the detail (#205 §4) ----

const HISTORY_500 = {
  page: 'This page of the history could not be read. Try another page or other filters.',
  detail: 'The detail of this correction could not be read.'
}

// Story 211 (RN4, #207 §4.1): `details.parameter` of a 400 of the search, named by the FILTER it belongs to (never by its
// code). `body`, `page`, `size` or an unknown one have no filter: «Invalid search.» alone.
const SEARCH_PARAMETER_LABEL = {
  deviceId: 'Machine',
  workerId: 'Worker',
  status: 'Status',
  kind: 'Type',
  window: 'Corrected stretch',
  'window.from': 'Corrected stretch',
  'window.to': 'Corrected stretch',
  requestedAt: 'Requested',
  'requestedAt.from': 'Requested',
  'requestedAt.to': 'Requested',
  text: 'Search'
}

const describeInvalidSearch = (details) => {
  const label = SEARCH_PARAMETER_LABEL[details.parameter]
  return label
    ? { key: 'Invalid search ({{parameter}}).', params: { parameter: label }, translate: ['parameter'], actions: [] }
    : { key: 'Invalid search.', params: {}, actions: [] }
}

/**
 * The message of a failed search (`target: 'page'`) or detail (`target: 'detail'`), after the one RETRY (RH-24). `translate`
 * names the params that are keys of the catalogue themselves (the label of a filter), translated before being inserted.
 */
export const describeHistoryFailure = (failure, target = 'page') => {
  if (failure.kind === 'springForbidden') return { key: 'You do not have permission to see the correction history.', params: {}, actions: [] }
  if (failure.kind === 'contract') {
    switch (failure.message) {
      case 'DATA_CORRECTION_ACTOR_NOT_A_USER':
        return { key: ACTOR_KEY, params: {}, actions: [] }
      case 'DATA_CORRECTION_RETRY':
        return { key: 'The history is busy. Try again in a moment.', params: {}, actions: ['TRY_AGAIN'] }
      case 'DATA_CORRECTION_NOT_FOUND':
        return { key: 'The correction no longer exists.', params: {}, actions: [] }
      case 'DATA_CORRECTION_INVALID_INTENT':
        return describeInvalidSearch(failure.details)
      case 'DATA_CORRECTION_UNEXPECTED_ERROR':
        return { key: HISTORY_500[target], params: {}, actions: ['TRY_AGAIN'] }
      default:
        return { key: 'Unexpected error ({{code}}).', params: { code: failure.message }, actions: [] }
    }
  }
  if (failure.status >= 500) return { key: HISTORY_500[target], params: {}, actions: ['TRY_AGAIN'] }
  return { key: 'Unexpected error ({{code}}).', params: { code: failure.message || failure.status }, actions: [] }
}

// ---- Revert (story 211: #203 §4, #210 C3, C6) ----

const REVERT_UNEXPECTED_KEY = 'The correction could not be reverted because of an unexpected error. Nothing was changed.'
const BUSY_KEY = 'The machine is busy with other operations. Try again in a moment.'
const CLOSED_COLUMNS = ['date_logout', 'end_date']
const LINKED_KEY = {
  REPORT: 'The correction created a clock-in that already has reported parts, so it can no longer be reverted.',
  STAND_BY: 'The correction created a clock-in that already has a stand-by, so it can no longer be reverted.'
}
/** RN17: a physical table of `details.table` (`device_worker`) by its label; an unknown one as it is, not translated. */
const tableParam = (table) => {
  const label = TABLE_LABEL[String(table || '').toUpperCase()]
  return label ? { value: label, translate: true } : { value: String(table || ''), translate: false }
}

const joined = (values) => (values || []).join(', ')

/**
 * RN15: the text of a revert blocker, the same as a blocker of the detail (RN10) and as an error of revert (RN14).
 * `{key, params, translate, extra, links}`: `translate` lists the params that are keys themselves (a table, a status);
 * `links` are the ids the text names, each one opens that correction (M:965); `extra` is a second sentence.
 */
export const describeBlocker = (blocker) => {
  const details = blocker.details || {}
  const message = (key, params = {}, more = {}) => ({ key, params, translate: [], ...more })
  switch (blocker.message) {
    case 'DATA_CORRECTION_REVERT_LATER_CORRECTIONS_FIRST':
      return message('Revert first the later corrections that share records with this one, in this order: {{ids}}.', { ids: joined(details.correctionIds) }, { links: details.correctionIds || [] })
    case 'DATA_CORRECTION_REVERT_CHILD_FIRST':
      return message('Revert first the trim of this close: {{ids}}.', { ids: joined(details.correctionIds) }, { links: details.correctionIds || [] })
    case 'DATA_CORRECTION_REVERT_ACTIVITY_AFTER_CLOSE_NOW': {
      const table = tableParam(details.table)
      return message('There was activity after the close ({{table}}: {{rowIds}}), so the «close now» can no longer be reverted.', { table: table.value, rowIds: joined(details.rowIds) }, { translate: table.translate ? ['table'] : [] })
    }
    case 'DATA_CORRECTION_REVERT_ROW_CHANGED': {
      const table = tableParam(details.table)
      const translate = table.translate ? ['table'] : []
      if (details.reason === 'ROW_MISSING') {
        return message('A record of this correction no longer exists ({{table}} {{rowId}}), so it can no longer be reverted.', { table: table.value, rowId: details.rowId }, { translate })
      }
      const columns = details.columns || []
      const closedAfterwards = columns.some((column) => CLOSED_COLUMNS.includes(column))
      return message('The plant changed a record of this correction ({{table}} {{rowId}}: {{columns}}), so it can no longer be reverted.', { table: table.value, rowId: details.rowId, columns: joined(columns) }, {
        translate,
        extra: closedAfterwards ? { key: 'The correction moved a record that was still open and the plant closed it afterwards.', params: {} } : undefined
      })
    }
    case 'DATA_CORRECTION_REVERT_ROW_REAPPEARED': {
      const table = tableParam(details.table)
      return message('A record this correction deleted exists again ({{table}} {{rowId}}), so it can no longer be reverted.', { table: table.value, rowId: details.rowId }, { translate: table.translate ? ['table'] : [] })
    }
    case 'DATA_CORRECTION_REVERT_ROW_LINKED':
      return LINKED_KEY[details.reason] ? message(LINKED_KEY[details.reason]) : message('Unexpected error ({{code}}).', { code: blocker.message })
    case 'DATA_CORRECTION_DEVICE_NOT_FOUND':
      return message('The machine does not exist.')
    case 'DATA_CORRECTION_NOT_REVERTIBLE': {
      // The statuses without a chip of their own are shown with their code (GATE2-H5).
      const label = STATUS_CHIP[details.status] && STATUS_CHIP[details.status].label
      return message('This correction can no longer be reverted ({{status}}).', { status: label || details.status }, { translate: label ? ['status'] : [] })
    }
    case 'DATA_CORRECTION_NOT_FOUND':
      return message('The correction no longer exists.')
    default:
      return message('Unexpected error ({{code}}).', { code: blocker.message })
  }
}

// The literals of revert that are blockers: their text, and the detail is read again (its blockers may have changed).
const REVERT_BLOCKERS = [
  'DATA_CORRECTION_REVERT_LATER_CORRECTIONS_FIRST', 'DATA_CORRECTION_REVERT_CHILD_FIRST', 'DATA_CORRECTION_REVERT_ACTIVITY_AFTER_CLOSE_NOW',
  'DATA_CORRECTION_REVERT_ROW_CHANGED', 'DATA_CORRECTION_REVERT_ROW_REAPPEARED', 'DATA_CORRECTION_REVERT_ROW_LINKED', 'DATA_CORRECTION_DEVICE_NOT_FOUND'
]
const REVERT_GONE = ['DATA_CORRECTION_NOT_REVERTIBLE', 'DATA_CORRECTION_NOT_FOUND']

/**
 * RN14: the message of a failed revert, after the one RETRY. Besides the text: `placement` (`confirmation`, or next to the
 * `reason` / `comment` field), `reload` (`NONE`, `DETAIL`, or `DETAIL_AND_PAGE`: the detail and page 0 of the history) and
 * `actions` (TRY_AGAIN after a second RETRY). No error closes the confirmation.
 */
export const describeRevertFailure = (failure) => {
  const message = (described, more = {}) => ({ translate: [], actions: [], placement: 'confirmation', reload: 'NONE', ...described, ...more })
  if (failure.kind === 'springForbidden') return message({ key: 'You do not have permission to revert corrections.', params: {} })
  if (failure.kind !== 'contract') return message({ key: REVERT_UNEXPECTED_KEY, params: {} })
  const code = failure.message
  if (code === 'DATA_CORRECTION_RETRY') return message({ key: BUSY_KEY, params: {} }, { actions: ['TRY_AGAIN'] })
  if (code === 'DATA_CORRECTION_ACTOR_NOT_A_USER') return message({ key: ACTOR_KEY, params: {} })
  if (code === 'DATA_CORRECTION_INVALID_INTENT') {
    switch (failure.details.reason) {
      case 'UNKNOWN_REASON':
        return message({ key: 'Choose a valid reason.', params: {} }, { placement: 'reason' })
      case 'COMMENT_REQUIRED':
        return message({ key: 'This reason requires a comment.', params: {} }, { placement: 'comment' })
      case 'COMMENT_TOO_LONG':
        return message({ key: 'The comment cannot be longer than {{max}} characters.', params: { max: failure.details.maxLength } }, { placement: 'comment' })
      default:
        return message({ key: REVERT_UNEXPECTED_KEY, params: {} })
    }
  }
  if (REVERT_BLOCKERS.includes(code)) return message(describeBlocker(failure), { reload: 'DETAIL' })
  if (REVERT_GONE.includes(code)) return message(describeBlocker(failure), { reload: 'DETAIL_AND_PAGE' })
  if (code === 'DATA_CORRECTION_UNEXPECTED_ERROR') return message({ key: REVERT_UNEXPECTED_KEY, params: {} })
  return message({ key: 'Unexpected error ({{code}}).', params: { code } })
}

// ---- Writing half: preview and apply (#201 §5) ----

const OPEN_KEY = 'This intervention is still open (the worker is still clocked in). Correcting an open record will come in a later version.'
const INCONSISTENT_KEY = 'This correction would leave inconsistent records ({{code}}). Choose another time or another type.'
const RELOAD = ['RELOAD_TIMELINE']

// message -> [key, actions]: the literals that need no parameter.
const SIMPLE_WRITE = {
  DATA_CORRECTION_ACTOR_NOT_A_USER: [ACTOR_KEY, []],
  DATA_CORRECTION_PLAN_CHANGED: ['The data changed since the preview. Check the new preview and apply again.', []],
  DATA_CORRECTION_RETRY: ['The machine is busy with other operations. Try again in a moment.', ['TRY_AGAIN']],
  DATA_CORRECTION_ANCHOR_NOT_FOUND: ['The record no longer exists. Reload the timeline.', RELOAD],
  DATA_CORRECTION_CONCURRENT_MODIFICATION: ['Someone changed these records in the meantime. Reload the timeline and try again.', RELOAD],
  DATA_CORRECTION_DEVICE_NOT_FOUND: ['The machine does not exist.', RELOAD],
  // Retired by #210, it keeps its key and its mapping (RN29).
  DATA_CORRECTION_CLOSE_NOW_NOT_SUPPORTED_YET: [OPEN_KEY, []],
  DATA_CORRECTION_CLOSE_NOW_NOT_CONFIRMED: ['Confirm that it is closed now before applying.', []],
  DATA_CORRECTION_INVALID_ROW_INTERVAL: ['A record of this intervention ends before it starts. Support must fix it before correcting.', []],
  DATA_CORRECTION_DUPLICATE_ACTIVE_ROWS: ['The job has duplicated running records on this machine in this stretch. They must be cleaned up before correcting.', []],
  DATA_CORRECTION_NOTHING_TO_CARVE: ['With this time nothing changes. Move the time or choose another type.', []],
  DATA_CORRECTION_UNEXPECTED_ERROR: ['Unexpected error. What you entered in the panel is kept, try again.', []]
}

const STRUCTURAL = ['DATA_CORRECTION_INVALID_INTERVAL', 'DATA_CORRECTION_ZERO_LENGTH_SEGMENT', 'DATA_CORRECTION_OPEN_SEGMENT_INSIDE_WINDOW', 'DATA_CORRECTION_OVERLAPPING_RESULT']
const CODE_PREFIX = 'DATA_CORRECTION_'

const LINKED_TEXT = {
  VOID: 'It cannot be voided because the intervention has reported parts. Shorten it instead.',
  STAND_BY: 'There is a clock-in linked to a stand-by in the stretch, so it cannot be cut.',
  OTHER_WORKER: 'Another worker (or nobody) reported parts in the stretch.',
  SEVERAL_LINKED: 'Several clock-ins with reported parts fall in the stretch.',
  NO_SURVIVOR: 'With this time no stretch remains to keep the reported parts.',
  LINKED_SURVIVOR: 'The stretch that remains already has reported parts.',
  BOTH_BOUNDS: 'The clock-in with reported parts crosses both edges of the stretch.'
}
const LINKED_WITH_REPORT_PHRASE = ['OTHER_WORKER', 'SEVERAL_LINKED', 'NO_SURVIVOR', 'LINKED_SURVIVOR', 'BOTH_BOUNDS']

/**
 * RH-19 (RN80): which instant to ask for. The report instant is the ORIGINAL end of the linked row (#191 RN67); the rows named in
 * `rowIds` that are not in the clock-in lane are ignored; none left = the same sentence without a time.
 */
const reportPhrase = (rowIds, { kind, format, imputationRows }) => {
  const ends = imputationRows
    .filter((row) => rowIds.includes(row.rowId) && row.end)
    .map((row) => parseIsoInstant(row.end).getTime())
  if (kind === 'ADJUST_START') {
    return ends.length > 0
      ? { key: 'Choose a time before the first report ({{time}}).', params: { time: format(Math.min(...ends)) } }
      : { key: 'Choose a time before the first report.', params: {} }
  }
  return ends.length > 0
    ? { key: 'Choose a time after the last report ({{time}}).', params: { time: format(Math.max(...ends)) } }
    : { key: 'Choose a time after the last report.', params: {} }
}

/** The ordinary acts of a «close now» (#210 C1), named as verbs inside a sentence (RN24). */
export const ACT_PHRASE = {
  INTERRUPT_JOB: 'pause the job',
  LOGOUT_WORKER: 'disconnect {{worker}}'
}

/** An act in the infinitive, translated, with the corrected worker; an unknown act by its code. */
export const actPhrase = (act, { t_, workerName }) => (ACT_PHRASE[act] ? t_(ACT_PHRASE[act], { worker: workerName }) : act)

const describeInvalidIntent = (failure, context) => {
  const details = failure.details
  const { format } = context
  switch (details.reason) {
    case 'NEW_INSTANT_OUTSIDE_INTERVAL':
      return { key: 'The time must be between {{start}} and {{end}}.', params: { start: format(parseIsoInstant(details.start).getTime()), end: format(parseIsoInstant(details.end).getTime()) } }
    case 'RESUME_AT_OUTSIDE_WINDOW':
      return { key: '«Back at…» must be between {{start}} and {{end}}.', params: { start: format(parseIsoInstant(details.windowStart).getTime()), end: format(parseIsoInstant(details.windowEnd).getTime()) } }
    case 'UNKNOWN_REASON':
      return { key: 'Choose a valid reason.', params: {}, placement: 'reason' }
    case 'COMMENT_REQUIRED':
      return { key: 'This reason requires a comment.', params: {}, placement: 'comment' }
    case 'COMMENT_TOO_LONG':
      return { key: 'The comment cannot be longer than {{max}} characters.', params: { max: details.maxLength }, placement: 'comment' }
    default:
      return { key: 'Invalid request ({{reason}}).', params: { reason: details.reason } }
  }
}

/**
 * The message of a rejected preview or apply (RN63, RN71-RN80). `context`:
 *   `t_`, `format(ms)`, `names` (worker, workers, device, devices; RN77), `workerName` (the corrected worker, GATE2-G10),
 *   `kind` and `tapes` of the draft, `imputationRows` (the rows of the clock-in lane of the block, for RN80).
 */
export const describeWriteFailure = (failure, context) => {
  const message = (key, params = {}, actions = [], extra = {}) => ({ key, params, actions, placement: 'panel', ...extra })
  const presenceOnly = context.tapes.includes('JOB_STATE') ? ['PRESENCE_ONLY'] : []
  if (failure.kind === 'springForbidden') return message('You do not have permission to correct timelines.')
  if (failure.kind !== 'contract') {
    return failure.status >= 500 || !failure.message
      ? message(SIMPLE_WRITE.DATA_CORRECTION_UNEXPECTED_ERROR[0])
      : message('Unexpected error ({{code}}).', { code: failure.message })
  }
  const details = failure.details
  const code = failure.message
  if (SIMPLE_WRITE[code]) return message(SIMPLE_WRITE[code][0], {}, SIMPLE_WRITE[code][1])
  if (STRUCTURAL.includes(code)) return message(INCONSISTENT_KEY, { code: code.slice(CODE_PREFIX.length) })
  switch (code) {
    case 'DATA_CORRECTION_INVALID_INTENT': {
      const described = describeInvalidIntent(failure, context)
      return message(described.key, described.params, [], described.placement ? { placement: described.placement } : {})
    }
    case 'DATA_CORRECTION_OPEN_ANCHOR':
      // Story 211 (RN29): an open record is corrected now; only adjusting its start is not possible.
      return details.reason === 'ADJUST_START_ON_OPEN'
        ? message('The start of an intervention that is still open cannot be adjusted. Use «Adjust end» or «Void».')
        : message('This intervention is still open. Reload the timeline.', {}, RELOAD)
    case 'DATA_CORRECTION_ORDINARY_ACT_REJECTED':
      // RN29 (A4): the act in the infinitive and the `cause` of Sun AS IT IS (free text, it is not translated).
      return message('Sun did not allow to {{act}}: {{cause}}. Nothing was changed.', { act: actPhrase(details.act, context), cause: details.cause || '' })
    case 'DATA_CORRECTION_ANCHOR_MISMATCH':
      return message('The record no longer matches this intervention ({{reason}}). Reload the timeline.', { reason: details.reason }, RELOAD)
    case 'DATA_CORRECTION_JOB_STATE_OUTSIDE_WORKER_SPAN': {
      const start = context.format(parseIsoInstant(details.spanStart).getTime())
      const end = details.spanEnd ? context.format(parseIsoInstant(details.spanEnd).getTime()) : context.t_('in progress')
      return message('{{worker}} left the job before the job record ended (their stretch {{span}}), so only their presence can be corrected.', { worker: context.workerName, span: `${start} – ${end}` }, presenceOnly)
    }
    case 'DATA_CORRECTION_OTHER_WORKERS_REPORTED_IN_WINDOW': {
      const workerIds = details.workerIds || []
      const head = workerIds.length > 0
        ? { key: '{{workers}} reported parts of this job inside the stretch, so the job state cannot be corrected.', params: { workers: context.names.workers(workerIds) } }
        : { key: 'Parts without a worker were reported in this job inside the stretch, so the job state cannot be corrected.', params: {} }
      return message(head.key, head.params, presenceOnly, { extra: context.kind === 'VOID' ? undefined : reportPhrase(details.rowIds || [], context) })
    }
    case 'DATA_CORRECTION_WORKER_NOT_PRESENT_AT_RESUME':
      return message('The worker was not connected to the machine at {{time}}. Change «Back at…» or leave it empty.', { time: context.format(parseIsoInstant(details.resumeAt).getTime()) })
    case 'DATA_CORRECTION_DEVICE_STATUS_GAP':
      return message('The machine has no recorded state at {{time}}. It must be fixed before correcting.', { time: context.format(parseIsoInstant(details.instant).getTime()) })
    case 'DATA_CORRECTION_LINKED_ROW_CANNOT_BE_REMOVED': {
      const text = LINKED_TEXT[details.reason]
      if (!text) return message(INCONSISTENT_KEY, { code: 'LINKED_ROW_CANNOT_BE_REMOVED' })
      const withPhrase = LINKED_WITH_REPORT_PHRASE.includes(details.reason) && context.kind !== 'VOID'
      const actions = details.reason === 'OTHER_WORKER' ? presenceOnly : []
      return message(text, {}, actions, { extra: withPhrase ? reportPhrase(details.rowIds || [], context) : undefined })
    }
    default:
      return message('Unexpected error ({{code}}).', { code })
  }
}

// The warnings of a preview (RN61, «Avisos y omisiones»): text + code in small.
const WARNING_TEXT = {
  REPORT_INSIDE_WINDOW: 'There are parts reported inside the stretch. Their date and the part history are not corrected in this version.',
  REPORT_LINK_KEPT: 'The intervention has reported parts. The link is kept because the clock-in is shortened, not deleted.',
  TERMINAL_AFTER_WINDOW: 'The job was finished after the stretch. That finish mark is not moved.'
}

/** `context`: `format`, `names`, `workerName`, and `jobName(id)` for the job of OTHER_WORKERS_ON_JOB. */
export const describeWarning = (warning, context) => {
  const details = warning.details || {}
  const { names, format } = context
  if (WARNING_TEXT[warning.code]) return { code: warning.code, key: WARNING_TEXT[warning.code], params: {} }
  switch (warning.code) {
    case 'OTHER_WORKERS_ON_JOB':
      return { code: warning.code, key: '{{workers}} also worked on {{job}} in that stretch. If you correct the job state it affects them. Correct only the presence if only {{worker}} made the mistake.', params: { workers: names.workers(details.workerIds || []), job: context.jobName(details.jobReferenceId), worker: context.workerName } }
    case 'OTHER_WORKERS_ON_DEVICE':
      return { code: warning.code, key: '{{workers}} also worked on the machine during part of the stretch. The machine state only changes to stopped where nobody was working.', params: { workers: names.workers(details.workerIds || []) } }
    case 'JOB_ACTIVE_ON_OTHER_DEVICE':
      return { code: warning.code, key: 'The job was also running on {{devices}} in that stretch. It is not corrected there.', params: { devices: names.devices(details.deviceIds || []) } }
    case 'AUTO_DEVICE_STATUS_NOT_CORRECTED':
      return { code: warning.code, key: 'The state of this machine is not corrected (mode {{mode}}).', params: { mode: details.mode } }
    case 'WORKER_OTHER_TASK_IN_WINDOW':
      return { code: warning.code, key: '{{workers}} had another task recorded in that stretch. It is not corrected.', params: { workers: names.workers(details.workerIds || []) } }
    case 'UNPAIRED_ACT_BOUNDARY':
      return { code: warning.code, key: 'Some cuts of the stretch could not be matched with their action ({{instants}}). Check the preview carefully.', params: { instants: (details.instants || []).map((instant) => format(parseIsoInstant(instant).getTime())).join(', ') } }
    default:
      return { code: warning.code, key: 'Warning {{code}}', params: { code: warning.code } }
  }
}

const OMISSION_TEXT = {
  RANGE_TOO_LONG: 'The correction spans more than 31 days, so the «after» cannot be drawn.',
  TOO_MANY_ROWS: 'There are too many records to draw the «after».'
}

/** The text that replaces the mini tapes when Sun could not draw the «after» (#201 §3). */
export const describeOmission = (omission) => ({ key: OMISSION_TEXT[omission] || 'Unexpected error ({{code}}).', params: { code: omission } })

/** The note shown in place of the «before» tape when its reading failed (A202-9, key nº 177). */
export const BEFORE_FAILED = { key: 'The «before» could not be loaded, so only the «after» is shown.', params: {} }
